/**
 * CourierScheduler — Receiver UX to schedule home delivery (pay-on-delivery).
 *
 * Inline panel shown alongside the receiver tracking screens. Posts to
 * /api/shipments/{tc}/courier-request. Pricing logic mirrors backend
 * (4.50€ base + 50% surcharge if desired_time < 4h ahead).
 */
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { TruckTrailer, Calendar, MapPinLine, CurrencyEur, X, CheckCircle } from "@phosphor-icons/react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const FEE_BASE = 4.5;

function defaultDesiredTime() {
  // Tomorrow 14:00 in user's local tz, formatted for `datetime-local`
  const d = new Date();
  d.setDate(d.getDate() + 1);
  d.setHours(14, 0, 0, 0);
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

export default function CourierScheduler({ trackingCode, defaultAddress = "" }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [existing, setExisting] = useState(null);
  const [address, setAddress] = useState(defaultAddress);
  const [desiredTime, setDesiredTime] = useState(defaultDesiredTime());
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  // Estimated fee (mirrors backend logic)
  const estimateFee = () => {
    try {
      const dt = new Date(desiredTime);
      const deltaH = (dt - new Date()) / 36e5;
      return deltaH < 4 ? +(FEE_BASE * 1.5).toFixed(2) : FEE_BASE;
    } catch { return FEE_BASE; }
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/shipments/${trackingCode}/courier-request`);
        if (!cancelled) setExisting(res.data?.courier_request || null);
      } catch (e) { /* ignore */ }
    })();
    return () => { cancelled = true; };
  }, [trackingCode]);

  const submit = async (e) => {
    e?.preventDefault();
    setBusy(true);
    setError("");
    try {
      const res = await axios.post(`${API}/shipments/${trackingCode}/courier-request`, {
        address: address.trim(), desired_time: desiredTime, note: note.trim(),
      });
      setExisting(res.data);
      setOpen(false);
    } catch (e) {
      setError(e?.response?.data?.detail || t("courier.error"));
    } finally {
      setBusy(false);
    }
  };

  const cancel = async () => {
    if (!window.confirm(t("courier.confirm_cancel"))) return;
    setBusy(true);
    try {
      await axios.delete(`${API}/shipments/${trackingCode}/courier-request`);
      setExisting(null);
    } catch (e) { setError(t("courier.error")); }
    finally { setBusy(false); }
  };

  // Already scheduled → show summary
  if (existing) {
    return (
      <div data-testid={`courier-${trackingCode}-existing`} className="bento-item p-5" style={{ marginTop: 16, border: "1px solid rgba(45,74,62,0.2)" }}>
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <TruckTrailer size={20} weight="duotone" style={{ color: "var(--primary)" }} />
            <span style={{ fontFamily: "Outfit", fontWeight: 700 }}>{t("courier.scheduled_title")}</span>
          </div>
          <span style={{ padding: "4px 10px", borderRadius: 9999, background: "rgba(232,163,89,0.1)", color: "var(--warning)", fontSize: "0.7rem", fontWeight: 700, textTransform: "uppercase" }}>
            {t("courier.pay_on_delivery")}
          </span>
        </div>
        <div className="grid grid-cols-2 gap-3 mb-3" style={{ fontSize: "0.85rem" }}>
          <div><span style={{ color: "var(--text-muted)", fontSize: "0.7rem", display: "block" }}>{t("courier.address")}</span><strong>{existing.address}</strong></div>
          <div><span style={{ color: "var(--text-muted)", fontSize: "0.7rem", display: "block" }}>{t("courier.time")}</span><strong>{new Date(existing.desired_time).toLocaleString("pt-PT", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" })}</strong></div>
        </div>
        <div className="flex items-center justify-between">
          <span style={{ fontSize: "0.85rem", color: "var(--text-muted)" }}>{t("courier.fee")}: <strong style={{ color: "var(--accent)" }}>€ {existing.fee_eur?.toFixed(2)}</strong></span>
          <button data-testid={`courier-${trackingCode}-cancel-btn`} onClick={cancel} disabled={busy} style={{ background: "none", border: "1px solid var(--border-color)", color: "var(--error)", padding: "6px 12px", borderRadius: 9999, cursor: "pointer", fontSize: "0.75rem", fontWeight: 600 }}>
            {t("courier.cancel")}
          </button>
        </div>
      </div>
    );
  }

  if (!open) {
    return (
      <button
        data-testid={`courier-${trackingCode}-open-btn`}
        onClick={() => setOpen(true)}
        className="flex items-center justify-center gap-2"
        style={{ marginTop: 16, padding: "12px 20px", borderRadius: 12, border: "1px dashed var(--primary)", background: "rgba(45,74,62,0.04)", color: "var(--primary)", fontFamily: "Outfit", fontWeight: 600, fontSize: "0.85rem", cursor: "pointer", width: "100%" }}
      >
        <TruckTrailer size={16} weight="duotone" />
        {t("courier.cta")}
      </button>
    );
  }

  return (
    <form onSubmit={submit} className="bento-item p-5" data-testid={`courier-${trackingCode}-form`} style={{ marginTop: 16 }}>
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <TruckTrailer size={20} weight="duotone" style={{ color: "var(--primary)" }} />
          <span style={{ fontFamily: "Outfit", fontWeight: 700 }}>{t("courier.form_title")}</span>
        </div>
        <button type="button" onClick={() => setOpen(false)} style={{ background: "none", border: "none", cursor: "pointer", color: "var(--text-muted)" }}>
          <X size={18} />
        </button>
      </div>

      <label className="block mb-3">
        <span className="flex items-center gap-1" style={{ fontSize: "0.7rem", color: "var(--text-muted)", textTransform: "uppercase", fontWeight: 700, letterSpacing: "0.05em", marginBottom: 4 }}><MapPinLine size={11} /> {t("courier.address")}</span>
        <input
          data-testid={`courier-${trackingCode}-address`}
          required
          minLength={4}
          value={address}
          onChange={(e) => setAddress(e.target.value)}
          placeholder={t("courier.address_placeholder")}
          style={{ width: "100%", padding: "10px 14px", borderRadius: 10, border: "1px solid var(--border-color)", background: "var(--bg)", fontSize: "0.85rem" }}
        />
      </label>

      <label className="block mb-3">
        <span className="flex items-center gap-1" style={{ fontSize: "0.7rem", color: "var(--text-muted)", textTransform: "uppercase", fontWeight: 700, letterSpacing: "0.05em", marginBottom: 4 }}><Calendar size={11} /> {t("courier.time")}</span>
        <input
          data-testid={`courier-${trackingCode}-time`}
          type="datetime-local"
          required
          value={desiredTime}
          onChange={(e) => setDesiredTime(e.target.value)}
          style={{ width: "100%", padding: "10px 14px", borderRadius: 10, border: "1px solid var(--border-color)", background: "var(--bg)", fontSize: "0.85rem" }}
        />
      </label>

      <label className="block mb-3">
        <span style={{ fontSize: "0.7rem", color: "var(--text-muted)", textTransform: "uppercase", fontWeight: 700, letterSpacing: "0.05em", marginBottom: 4, display: "block" }}>{t("courier.note_optional")}</span>
        <input
          data-testid={`courier-${trackingCode}-note`}
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder={t("courier.note_placeholder")}
          style={{ width: "100%", padding: "10px 14px", borderRadius: 10, border: "1px solid var(--border-color)", background: "var(--bg)", fontSize: "0.85rem" }}
        />
      </label>

      <div className="flex items-center justify-between" style={{ padding: "10px 14px", borderRadius: 10, background: "rgba(217,108,74,0.06)", marginBottom: 12 }}>
        <span className="flex items-center gap-1" style={{ fontSize: "0.78rem", color: "var(--text-muted)" }}><CurrencyEur size={12} /> {t("courier.fee_estimate")}</span>
        <span style={{ fontFamily: "Outfit", fontWeight: 700, color: "var(--accent)" }}>€ {estimateFee().toFixed(2)}</span>
      </div>
      <p style={{ fontSize: "0.7rem", color: "var(--text-muted)", marginBottom: 12 }}>{t("courier.pay_at_delivery_hint")}</p>

      <button
        data-testid={`courier-${trackingCode}-submit-btn`}
        type="submit"
        disabled={busy || address.trim().length < 4}
        className="flex items-center justify-center gap-2"
        style={{ width: "100%", padding: "12px 20px", borderRadius: 12, border: "none", background: busy ? "var(--text-muted)" : "var(--primary)", color: "white", fontFamily: "Outfit", fontWeight: 600, cursor: busy ? "default" : "pointer", fontSize: "0.85rem", opacity: busy ? 0.7 : 1 }}
      >
        <CheckCircle size={16} weight="bold" /> {busy ? t("courier.submitting") : t("courier.confirm")}
      </button>
      {error && <p style={{ marginTop: 8, fontSize: "0.78rem", color: "var(--error)" }}>{error}</p>}
    </form>
  );
}
